import { Component } from '@theme/component';

/**
 * Slide-in promotional popup with a collapsible "reminder" mini tab.
 *
 * Behavior:
 *  - After a configurable delay the panel slides in from the docked edge.
 *  - Closing the panel collapses it into a small mini tab that can reopen it.
 *  - Dismissal is remembered (session or N days) so it does not reappear on
 *    every visit. In the theme editor it always shows and never persists.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} panel
 * @property {HTMLElement} [mini]
 *
 * @extends {Component<Refs>}
 */
export class PromoPopup extends Component {
  requiredRefs = ['panel'];

  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();

    this.#storageKey = `promo-popup:${this.dataset.sectionId}`;

    if (this.#isEditor) {
      // Always reveal while editing; never persist dismissals.
      this.open();
      return;
    }

    if (!this.#shouldShow()) {
      // Already closed on a previous page (or earlier this session): keep it
      // collapsed as the mini reminder across navigation until the visitor
      // clicks to reopen. Show it instantly (no entrance animation) so it
      // reads as a persisted state rather than a fresh close.
      const mini = this.refs.mini;
      if (mini) {
        mini.style.transition = 'none';
        mini.classList.add('promo-popup__mini--visible');
        void mini.offsetWidth;
        mini.style.transition = '';
      }
      return;
    }

    const delay = parseInt(this.dataset.delay ?? '1000', 10);
    this.#timer = window.setTimeout(() => this.open(), Number.isNaN(delay) ? 1000 : delay);

    document.addEventListener('keydown', this.#handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#timer) clearTimeout(this.#timer);
    document.removeEventListener('keydown', this.#handleKeydown);
  }

  /** Reveal the full panel. */
  open() {
    this.refs.panel.classList.remove('promo-popup__panel--intro', 'promo-popup__panel--hidden');
    this.refs.mini?.classList.remove('promo-popup__mini--visible');
  }

  /**
   * Close the panel and collapse to the mini tab (if present).
   * @param {Event} [event]
   */
  close(event) {
    event?.preventDefault();
    this.refs.panel.classList.add('promo-popup__panel--hidden');

    if (this.refs.mini) {
      window.setTimeout(() => this.refs.mini?.classList.add('promo-popup__mini--visible'), 350);
    }

    this.#persistDismissal();
  }

  /**
   * Hide the mini tab and bring the full panel back.
   * @param {Event} [event]
   */
  reopen(event) {
    event?.preventDefault();
    this.refs.mini?.classList.remove('promo-popup__mini--visible');
    window.setTimeout(() => this.open(), 220);
  }

  /** @type {string} */
  #storageKey = 'promo-popup';

  get #isEditor() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key === 'Escape' && !this.refs.panel.classList.contains('promo-popup__panel--hidden')) {
      this.close();
    }
  };

  /**
   * Decide whether the popup should appear on this visit.
   * @returns {boolean}
   */
  #shouldShow() {
    const frequency = this.dataset.frequency ?? 'days';
    if (frequency === 'every_visit') return true;

    try {
      if (frequency === 'session') {
        return sessionStorage.getItem(this.#storageKey) == null;
      }

      // 'days': re-show only after the window has elapsed since the last dismissal.
      const record = localStorage.getItem(this.#storageKey);
      if (!record) return true;

      const days = parseInt(this.dataset.days ?? '7', 10);
      const dismissedAt = parseInt(record, 10);
      if (Number.isNaN(dismissedAt)) return true;

      return Date.now() - dismissedAt > (Number.isNaN(days) ? 7 : days) * 86400000;
    } catch {
      // Storage blocked (private mode, etc.) — fail open and show it.
      return true;
    }
  }

  /** Record the dismissal so the popup respects the chosen frequency. */
  #persistDismissal() {
    if (this.#isEditor) return;
    const frequency = this.dataset.frequency ?? 'days';

    try {
      if (frequency === 'session') {
        sessionStorage.setItem(this.#storageKey, '1');
      } else if (frequency === 'days') {
        localStorage.setItem(this.#storageKey, String(Date.now()));
      }
    } catch {
      // Ignore storage failures.
    }
  }
}

if (!customElements.get('promo-popup')) {
  customElements.define('promo-popup', PromoPopup);
}
